import { useEffect, useState } from 'react';

/* Reveal elements marked `.reveal` as they scroll into view.
   Replaces the old jQuery scroll handlers with one IntersectionObserver. */
export function useRevealOnScroll() {
  useEffect(() => {
    const nodes = document.querySelectorAll('.reveal:not(.is-visible)');
    if (!nodes.length) return;

    if (!('IntersectionObserver' in window)) {
      nodes.forEach((n) => n.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
    );

    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);
}

/* True once the page has scrolled past `offset` — drives the sticky header. */
export function useScrolled(offset = 40) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > offset);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [offset]);

  return scrolled;
}

/* Id of the section currently in view — drives the nav underline. */
export function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);

  useEffect(() => {
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!sections.length) return;

    const onScroll = () => {
      const line = window.scrollY + window.innerHeight * 0.35;
      let current = ids[0];
      sections.forEach((section) => {
        if (section.offsetTop <= line) current = section.id;
      });
      setActive(current);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [ids]);

  return active;
}

/* Cycles through `words`, typing and erasing. Replaces react-typing-effect
   so the hero keeps a stable height and respects reduced motion. */
export function useTypewriter(words, { type = 70, erase = 38, hold = 1900 } = {}) {
  const [index, setIndex] = useState(0);
  const [text, setText] = useState(words[0] || '');
  const [erasing, setErasing] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || words.length < 2) return;

    const word = words[index];
    let delay;

    if (!erasing && text === word) {
      delay = hold;
    } else if (erasing && text === '') {
      delay = 220;
    } else {
      delay = erasing ? erase : type;
    }

    const timer = setTimeout(() => {
      if (!erasing && text === word) {
        setErasing(true);
      } else if (erasing && text === '') {
        setErasing(false);
        setIndex((i) => (i + 1) % words.length);
      } else if (erasing) {
        setText(word.slice(0, text.length - 1));
      } else {
        setText(word.slice(0, text.length + 1));
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [text, erasing, index, words, type, erase, hold]);

  return text;
}
